import { h, render } from "vue";

import SCADragGhost from "./SCADragGhost.vue";
import type { DragGhostStartOptions } from "./types";

/**
 * The app-wide drag preview for native HTML5 drags.
 *
 * One call integrates a site: `dragGhostStart` in `dragstart`. Everything after that — following the
 * pointer, telling a landed drag from a cancelled one, tearing down — this module observes from its
 * own document listeners, so a drop handler has nothing to remember and nothing to leak.
 *
 * The native drag image is suppressed at every site. Letting the browser rasterize the dragged node
 * cost 900ms-1.2s synchronously inside `dragstart` on an AG Grid row, during which no `dragover`
 * can run and the cursor stays "not-allowed" (#3387). Nothing here reads or clones the dragged
 * element: the ghost is built from the strings the caller already has.
 */

const POINTER_OFFSET_PX = 4;

const ENTER_MS = 120;
const DIVE_MS = 180;

const ENTER_EASING = "cubic-bezier(0.2, 0, 0, 1)";
/** Accelerating, so the ghost reads as pulled into the target rather than slid across to it. */
const DIVE_EASING = "cubic-bezier(0.32, 0, 0.67, 0)";

const LISTENER_OPTIONS = { capture: true, passive: true } as const;

/**
 * The drag loop fires `drag`/`dragover` every ~350ms while a drag is live, even with the pointer
 * still and even once it has left the window, so silence this long means the drag ended somewhere
 * the page never saw — a drop into another application, an OS-cancelled session — and neither
 * `drop` nor `dragend` is coming.
 */
const STALE_DRAG_MS = 5000;
const STALE_CHECK_MS = 1000;

/**
 * How close to the viewport edge the last `dragover` has to be for a trailing `dragleave` to count
 * as leaving the window. A `dragleave` deeper inside is a drop released over nothing, which keeps
 * its exit animation.
 */
const WINDOW_EDGE_PX = 64;

/**
 * Transparent 1x1 image handed to `setDragImage`, decoded at import time rather than inside
 * `dragstart`.
 *
 * `setDragImage` silently does nothing for an image that has not finished loading, and an image
 * whose `src` was assigned in the same tick has not. The browser then draws its own drag image
 * beside our ghost — a globe, for a row whose name is a link, since the browser puts `text/uri-list`
 * on the drag by itself. Decoding it here means it is ready long before anyone can start a drag.
 *
 * A canvas would sidestep loading entirely, but Chrome refuses one that is not in the document,
 * which is worse: the suppression then never works at all.
 */
const blankDragImage: HTMLImageElement | null = typeof document === "undefined" ? null : new Image();
if (blankDragImage) {
  blankDragImage.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7";
}

let hostEl: HTMLElement | null = null;
let surfaceEl: HTMLElement | null = null;
let exitingEl: HTMLElement | null = null;
let pointerX = 0;
let pointerY = 0;
let hostOriginX = 0;
let hostOriginY = 0;
let frameId: number | null = null;
let landed = false;
let hiddenOutside = false;
let leaveTimer: ReturnType<typeof setTimeout> | null = null;
let reducedMotion = false;
let lastPointerAt = 0;
let staleTimer: ReturnType<typeof setInterval> | null = null;
let sourceEl: EventTarget | null = null;

/** Hands the browser a blank drag image, so it skips rasterizing the dragged node. */
function suppressNativeDragImage(event: DragEvent): void {
  if (!event.dataTransfer || !blankDragImage) return;

  event.dataTransfer.setDragImage(blankDragImage, 0, 0);
}

/** Shows the drag preview. Call inside `dragstart`, after the payload has been written. */
export function dragGhostStart(options: DragGhostStartOptions): void {
  teardown();
  suppressNativeDragImage(options.event);

  landed = false;
  hiddenOutside = false;
  reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  pointerX = options.event.clientX;
  pointerY = options.event.clientY;

  hostEl = document.createElement("div");
  hostEl.style.cssText =
    "position:fixed;top:0;left:0;right:auto;bottom:auto;margin:0;padding:0;border:none;background:transparent;" +
    "overflow:visible;z-index:10000;pointer-events:none;will-change:transform;contain:layout style;" +
    "transform-origin:0 0;";
  document.body.appendChild(hostEl);

  // A native `[popover]` (e.g. the overflow tabs menu) paints in the browser's top layer, which
  // sits above the entire regular document regardless of z-index — a plain z-index can never win
  // against it. Promoting the ghost into the top layer too, `"manual"` so it never auto-dismisses
  // an already-open popover, puts it above whatever popover the drag started from or crosses over.
  // The inline reset above clears the UA popover stylesheet's own `inset`/`margin`/`border`/
  // `background`, which would otherwise center and box the host instead of leaving it exactly
  // where `writePosition()` places it.
  if (typeof hostEl.showPopover === "function") {
    hostEl.setAttribute("popover", "manual");
    try {
      hostEl.showPopover();
    } catch {
      // Some contexts (e.g. an ancestor already mid-transition) can refuse to show a new popover;
      // the ghost still renders, just possibly beneath another top-layer element — not worse than
      // before this fix.
    }
  }

  // Pinch-zoomed Safari measures client coordinates from the visual viewport but anchors fixed
  // boxes to the layout viewport, so the host's origin is not at client 0/0 there.
  const hostOrigin = hostEl.getBoundingClientRect();
  hostOriginX = hostOrigin.left;
  hostOriginY = hostOrigin.top;
  writePosition();

  render(h(SCADragGhost, { item: options.item, totalCount: options.totalCount ?? 1, stack: options.stack }), hostEl);
  surfaceEl = hostEl.firstElementChild as HTMLElement | null;

  animate(surfaceEl, { transform: ["scale(0.92)", "scale(1)"], opacity: [0, 1] }, ENTER_MS, ENTER_EASING);

  document.addEventListener("dragover", onDragOver, LISTENER_OPTIONS);
  document.addEventListener("drag", onDrag, LISTENER_OPTIONS);
  document.addEventListener("dragleave", onDragLeave, LISTENER_OPTIONS);
  document.addEventListener("dragend", onDragEnd, LISTENER_OPTIONS);
  document.addEventListener("drop", onDocumentDrop, LISTENER_OPTIONS);
  document.addEventListener("keydown", onKeyDown, LISTENER_OPTIONS);

  // `dragend` is fired at the source element even once it has left the DOM mid-drag (a view that
  // swapped its rows under the pointer); only a listener on the element itself still hears it then.
  sourceEl = options.event.target;
  sourceEl?.addEventListener("dragend", onDragEnd, LISTENER_OPTIONS);

  lastPointerAt = Date.now();
  staleTimer = setInterval(onStaleCheck, STALE_CHECK_MS);
}

/** The host transform that puts its top-left corner at client point `x`/`y`. */
function translateTo(x: number, y: number): string {
  return `translate3d(${x - hostOriginX}px, ${y - hostOriginY}px, 0)`;
}

function writePosition() {
  frameId = null;
  if (!hostEl) return;
  hostEl.style.transform = translateTo(pointerX + POINTER_OFFSET_PX, pointerY + POINTER_OFFSET_PX);
}

/** Firefox reports 0/0 on `drag`, and so does Chrome's last `drag` before `dragend` — except pinch
 *  zoom shifts Chrome's client point, leaving only its screen point at 0/0. Either snaps the ghost
 *  to the corner. */
function hasUsablePoint(event: DragEvent): boolean {
  if (event.clientX === 0 && event.clientY === 0) return false;
  return !(event.screenX === 0 && event.screenY === 0);
}

function isOutsideViewport(event: DragEvent): boolean {
  return (
    event.clientX <= 0 ||
    event.clientY <= 0 ||
    event.clientX >= window.innerWidth ||
    event.clientY >= window.innerHeight
  );
}

/** Returns whether the event carried a point the ghost can follow. */
function trackPointer(event: DragEvent): boolean {
  // Before the coordinate filter: a 0/0 `drag` still proves the drag is alive.
  lastPointerAt = Date.now();
  if (!hasUsablePoint(event)) return false;

  pointerX = event.clientX;
  pointerY = event.clientY;
  frameId ??= requestAnimationFrame(writePosition);
  return true;
}

function isLastPointNearViewportEdge(): boolean {
  return (
    pointerX < WINDOW_EDGE_PX ||
    pointerY < WINDOW_EDGE_PX ||
    pointerX > window.innerWidth - WINDOW_EDGE_PX ||
    pointerY > window.innerHeight - WINDOW_EDGE_PX
  );
}

function clearLeaveTimer() {
  if (leaveTimer === null) return;
  clearTimeout(leaveTimer);
  leaveTimer = null;
}

function onDragOver(event: Event) {
  clearLeaveTimer();
  trackPointer(event as DragEvent);
  if (!hiddenOutside || !hostEl) return;

  hiddenOutside = false;
  writePosition();
  hostEl.style.visibility = "";
}

/** A page-drawn ghost cannot follow the pointer past the window, so it hides there instead of
 *  freezing at the edge. */
function hideOutside() {
  if (hiddenOutside || !hostEl) return;
  hiddenOutside = true;
  hostEl.style.visibility = "hidden";
}

function onDrag(event: Event) {
  const dragEvent = event as DragEvent;
  if (trackPointer(dragEvent) && isOutsideViewport(dragEvent)) hideOutside();
}

/**
 * While the pointer is still in the page, every `dragleave` is followed by a `dragover` in the same
 * task, so one still unanswered on the next task means the pointer left the window. The point the
 * `dragleave` carries cannot tell: Chrome and Safari repeat the last one inside the page there.
 */
function onDragLeave(event: Event) {
  if (!isOutsideViewport(event as DragEvent) && !isLastPointNearViewportEdge()) return;
  leaveTimer ??= setTimeout(() => {
    leaveTimer = null;
    hideOutside();
  }, 0);
}

function onDragEnd() {
  detachListeners();
  playExit();
}

function onStaleCheck() {
  if (Date.now() - lastPointerAt < STALE_DRAG_MS) return;
  onDragEnd();
}

/**
 * On macOS, Chrome/Safari run native HTML5 drags through AppKit's `NSDraggingSession`. Escape cancels
 * that session at the OS level before the page's JS ever sees a `keydown` — the browser only learns
 * the drag is over, and fires `dragend`, once Cocoa's own (fixed-duration, unconfigurable) reject
 * animation has finished. That gap sits entirely before this listener can run, so it cannot close it;
 * it only catches platforms where Escape *does* reach the page during a live drag.
 */
function onKeyDown(event: Event) {
  if ((event as KeyboardEvent).key !== "Escape") return;
  onDragEnd();
}

/**
 * The pointer was released over a drop zone, rather than the drag being cancelled.
 *
 * Capture phase on `document`, so no site can hide a drop from it with `stopPropagation` — that is
 * the whole point of observing it here instead of asking every drop handler to report in.
 *
 * The deferred teardown is a safety net: `dragend` normally follows a drop, but not every engine fires
 * it once the drop removed the source element from the DOM.
 */
function onDocumentDrop() {
  landed = true;
  const host = hostEl;
  setTimeout(() => {
    if (host && hostEl === host) onDragEnd();
  }, 0);
}

function detachListeners() {
  document.removeEventListener("dragover", onDragOver, LISTENER_OPTIONS);
  document.removeEventListener("drag", onDrag, LISTENER_OPTIONS);
  document.removeEventListener("dragleave", onDragLeave, LISTENER_OPTIONS);
  clearLeaveTimer();
  document.removeEventListener("dragend", onDragEnd, LISTENER_OPTIONS);
  document.removeEventListener("drop", onDocumentDrop, LISTENER_OPTIONS);
  document.removeEventListener("keydown", onKeyDown, LISTENER_OPTIONS);
  sourceEl?.removeEventListener("dragend", onDragEnd, LISTENER_OPTIONS);
  sourceEl = null;

  if (frameId !== null) {
    cancelAnimationFrame(frameId);
    frameId = null;
  }

  if (staleTimer !== null) {
    clearInterval(staleTimer);
    staleTimer = null;
  }
}

function playExit() {
  const host = hostEl;
  const surface = surfaceEl;
  if (!host) return;

  hostEl = null;
  surfaceEl = null;

  if (reducedMotion || hiddenOutside) {
    destroy(host);
    return;
  }

  // A landed ghost shrinks where it was released: that is already the spot the user aimed at, and
  // the drop target is usually the row right under it. A cancelled drag has nowhere in particular to
  // land, so it dives off the bottom of the screen instead of shrinking in place mid-air.
  const from = host.style.transform;
  const to = landed
    ? `${from} scale(0.4)`
    : `${translateTo(pointerX + POINTER_OFFSET_PX, window.innerHeight)} scale(0.4)`;
  const hostAnimation = animate(host, { transform: [from, to] }, DIVE_MS, DIVE_EASING);
  animate(surface, { opacity: [1, 0] }, DIVE_MS, DIVE_EASING);

  if (!hostAnimation) {
    destroy(host);
    return;
  }

  exitingEl = host;
  const finish = () => {
    if (exitingEl === host) exitingEl = null;
    destroy(host);
  };
  hostAnimation.onfinish = finish;
  hostAnimation.oncancel = finish;
}

function animate(
  element: HTMLElement | null,
  keyframes: PropertyIndexedKeyframes,
  duration: number,
  easing: string,
): Animation | null {
  if (!element || typeof element.animate !== "function") return null;
  return element.animate(keyframes, { duration, easing, fill: "forwards" });
}

function destroy(element: HTMLElement) {
  // Removing a showing popover from the document implicitly hides it per spec, but that relies on
  // every engine's removal algorithm agreeing — hide it explicitly first rather than lean on that.
  try {
    element.hidePopover?.();
  } catch {
    // Already hidden, or never successfully shown (see `showPopover`'s own try/catch) — nothing to
    // undo either way.
  }
  render(null, element);
  element.remove();
}

/** Drops any ghost still on screen, animating out or not, without waiting. Runs before every drag. */
function teardown() {
  detachListeners();

  const host = hostEl;
  const exiting = exitingEl;
  hostEl = null;
  surfaceEl = null;
  exitingEl = null;
  landed = false;
  hiddenOutside = false;

  if (host) destroy(host);
  if (exiting) destroy(exiting);
}
